import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import dayjs from 'dayjs'

import { getErrorMessage, relativeOrDateTime } from 'utils'

import { useSnackbar } from 'contexts'
import { useExtendTrial } from 'hooks/api'
import { DemoListItem } from 'services/demosApi'

import { Stack, Typography } from '@mui/material'

import { Button, Modal } from '@repo/ui'
import { CompanyBlock, DatePickerField, SelectField } from 'components'

interface ExtendTrialModalProps {
  demo: DemoListItem | null
  open: boolean
  onClose: () => void
}

/**
 * No "Always" option here, unlike `ExtendDemoModal`'s `EXTEND_LIFETIME_OPTIONS`.
 * The API REQUIRES an absolute `trialEndsAt` (`ExtendTrialUseCase`'s own doc:
 * "an unbounded console trial contradicts the whole point of building one") —
 * so there is no neutral preset to default to. The empty string is a real
 * "nothing chosen yet" state, not a fourth lifetime.
 */
type TrialLifetime = '7' | '14' | '30' | 'custom' | ''

interface TrialFormValues {
  lifetime: TrialLifetime
  customDate: string
}

const TRIAL_LIFETIME_OPTIONS = [
  { label: '7 days', value: '7' },
  { label: '14 days (default)', value: '14' },
  { label: '30 days', value: '30' },
  { label: 'Custom date', value: 'custom' as const },
]

const DATE_FORMAT = 'YYYY-MM-DD'

// Deliberately empty, not '14': there is no neutral default to reopen on (see
// the TrialLifetime comment above), so the AE must make an explicit choice
// every time, exactly as `ExtendDemoModal` requires for its custom-date branch.
const DEFAULTS: TrialFormValues = { lifetime: '', customDate: '' }

/** Presets count from NOW — same reasoning as `ExtendDemoModal`'s `toExpiryIso`. */
const toTrialEndsAtIso = (values: TrialFormValues): string | null => {
  if (!values.lifetime) return null
  if (values.lifetime === 'custom') {
    if (!values.customDate) return null
    return dayjs(values.customDate).endOf('day').toDate().toISOString()
  }
  const days = Number(values.lifetime)
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
}

/**
 * VRG-361 — change a demo's CONSOLE TRIAL deadline after the fact, "just like
 * we comp plans" (the ticket's own framing). This is the invited admin's
 * console-access clock, a DIFFERENT clock from `ExtendDemoModal`'s
 * `demoExpiresAt` (the public certificate's lifetime) — the two are
 * deliberately independent and this modal never touches the other's field.
 *
 * Works whether or not the trial has started yet (`trialStartedAt` null): an
 * AE can pre-set a longer deadline in advance of a Tier-1 physical send that
 * will take weeks to arrive, exactly as `ExtendTrialUseCase` documents. So the
 * kebab item that opens this stays enabled in that state too — see
 * `InternalDemosPage`'s `getActionItems` for the record-state reasoning.
 */
export const ExtendTrialModal = ({ demo, open, onClose }: ExtendTrialModalProps) => {
  const { open: notify } = useSnackbar()
  const { mutate: extend, isPending } = useExtendTrial()

  const methods = useForm<TrialFormValues>({ defaultValues: DEFAULTS })
  const { control, watch, reset, handleSubmit } = methods
  // Same narrowing as ExtendDemoModal, for the same reason: DatePickerField
  // types against RHF's untyped default generic.
  const untypedFieldMethods: any = methods

  const lifetime = watch('lifetime')
  const customDate = watch('customDate')

  // Reset per opening — otherwise the modal reopens holding the PREVIOUS
  // demo's picked value, which reads as a preselection of this one's setting.
  useEffect(() => {
    if (open) reset(DEFAULTS)
  }, [open, reset])

  const onValid = (values: TrialFormValues) => {
    if (!demo) return
    const trialEndsAt = toTrialEndsAtIso(values)
    if (!trialEndsAt) return
    extend(
      { companyId: demo.companyId, trialEndsAt },
      {
        onSuccess: () => {
          notify({ type: 'success', title: 'Console trial deadline updated' })
          onClose()
        },
        onError: (error) => {
          notify({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  const current = !demo?.trialStartedAt
    ? demo?.trialEndsAt
      ? `Not started. Pre-set to end ${relativeOrDateTime(demo.trialEndsAt)} once accepted.`
      : 'Not started. No admin has accepted the invite yet.'
    : demo.trialEndsAt
      ? `Started ${relativeOrDateTime(demo.trialStartedAt)}, ends ${relativeOrDateTime(demo.trialEndsAt)}.`
      : `Started ${relativeOrDateTime(demo.trialStartedAt)}.`

  // No lifetime picked yet, or "custom" picked with no date — either way there
  // is nothing valid to send, and the endpoint requires the field be present.
  const incomplete = !lifetime || (lifetime === 'custom' && !customDate)

  return (
    <Modal
      title="Extend trial"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onValid)}
            disabled={isPending || incomplete}
            loading={isPending}
          >
            Update trial
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {demo && <CompanyBlock company={{ name: demo.companyName, logo: demo.logo }} />}

        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Currently: {current} This is the invited admin's console access, not
          the public certificate's expiry. A preset counts from today.
        </Typography>

        <Controller
          name="lifetime"
          control={control}
          render={({ field }) => (
            <SelectField
              label="New trial length"
              value={field.value}
              onChange={(v) => field.onChange(v as TrialLifetime)}
              options={TRIAL_LIFETIME_OPTIONS}
              placeholder="Select a length"
            />
          )}
        />

        {lifetime === 'custom' && (
          <DatePickerField
            {...untypedFieldMethods}
            name="customDate"
            label="Trial ends on"
            format={DATE_FORMAT}
            // Yesterday, so TODAY is selectable — a same-day deadline is a
            // legitimate "stop console access at end of day".
            minDate={dayjs().subtract(1, 'day')}
          />
        )}
      </Stack>
    </Modal>
  )
}
